/**
 * Concept Graph API Service
 *
 * Handles all API communication for the concept graph feature.
 * Provides methods to fetch concepts, relationships, and implementations.
 *
 * @module concept-graph/services/concept-service
 */

import type {
  Concept,
  ConceptCategory,
  ProgrammingLanguage,
  LanguageImplementation
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Query parameters for listing concepts
 */
export interface ConceptListParams {
  category?: ConceptCategory;
  difficulty?: 1 | 2 | 3 | 4 | 5;
  language?: ProgrammingLanguage;
  page?: number;
  limit?: number;
}

/**
 * Response from GET /api/concepts
 */
export interface ConceptListResponse {
  concepts: Concept[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  metadata?: {
    version: string;
    totalConcepts: number;
    languages: string[];
    lastUpdated: string;
  };
}

/**
 * Related concept with relationship info
 */
export interface RelatedConceptInfo {
  concept: Concept;
  relationship: {
    targetConceptId: string;
    relationshipType: string;
    strength: number;
    explanation: string;
    bidirectional: boolean;
  };
}

/**
 * Response from GET /api/concepts/:id/related
 */
export interface RelatedConceptsResponse {
  concept: Concept;
  related: RelatedConceptInfo[];
  depth: number;
}

/**
 * Response from GET /api/concepts/:id/implementations/:lang
 */
export interface ImplementationResponse {
  conceptId: string;
  conceptName: string;
  language: string;
  implementation: LanguageImplementation;
}

/**
 * Fetch all concepts with optional filters
 */
export async function fetchConcepts(params: ConceptListParams = {}): Promise<ConceptListResponse> {
  const queryParams = new URLSearchParams();

  if (params.category) queryParams.set('category', params.category);
  if (params.difficulty) queryParams.set('difficulty', params.difficulty.toString());
  if (params.language) queryParams.set('language', params.language);
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.limit) queryParams.set('limit', params.limit.toString());

  const url = `${API_BASE_URL}/api/concepts?${queryParams.toString()}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch concepts: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch a single concept by ID
 */
export async function fetchConcept(conceptId: string): Promise<Concept> {
  const url = `${API_BASE_URL}/api/concepts/${conceptId}`;

  const response = await fetch(url);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Concept not found: ${conceptId}`);
    }
    throw new Error(`Failed to fetch concept: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch related concepts
 */
export async function fetchRelatedConcepts(
  conceptId: string,
  options: {
    depth?: number;
    types?: string[];
  } = {}
): Promise<RelatedConceptsResponse> {
  const queryParams = new URLSearchParams();

  if (options.depth) queryParams.set('depth', options.depth.toString());
  if (options.types) queryParams.set('types', options.types.join(','));

  const url = `${API_BASE_URL}/api/concepts/${conceptId}/related?${queryParams.toString()}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch related concepts: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch language-specific implementation
 */
export async function fetchImplementation(
  conceptId: string,
  language: ProgrammingLanguage
): Promise<ImplementationResponse> {
  const url = `${API_BASE_URL}/api/concepts/${conceptId}/implementations/${language}`;

  const response = await fetch(url);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Implementation not found: ${conceptId} in ${language}`);
    }
    throw new Error(`Failed to fetch implementation: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch available categories
 */
export async function fetchCategories(): Promise<{
  categories: ConceptCategory[];
  counts: Record<string, number>;
}> {
  const url = `${API_BASE_URL}/api/concepts/meta/categories`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch categories: ${response.statusText}`);
  }

  return response.json();
}
